import React, {useState, useEffect} from 'react'
import {Modal, Button, Spinner} from 'react-bootstrap'
import {useIntl} from 'react-intl'
import {useAuth} from '../../auth'
import {RemovedProduct} from '../core/_models'
import {
  getRemovedProductsNeedingConfirmation,
  confirmRemovedProduct,
  checkRemovedProducts,
} from '../core/_requests'
import {toast} from 'react-toastify'

interface RemovedProductsBlockingModalProps {
  show: boolean
  onAllConfirmed: () => void
}

const RemovedProductsBlockingModal: React.FC<RemovedProductsBlockingModalProps> = ({
  show,
  onAllConfirmed,
}) => {
  const {formatMessage, locale: language} = useIntl()
  const {auth} = useAuth()
  const [products, setProducts] = useState<RemovedProduct[]>([])
  const [currentIndex, setCurrentIndex] = useState(0)
  const [loading, setLoading] = useState(false)
  const [confirming, setConfirming] = useState(false)

  const fetchUnconfirmedProducts = async () => {
    if (!auth?.token) return

    setLoading(true)
    try {
      const response = await getRemovedProductsNeedingConfirmation(auth.token, language, false)
      setProducts(response.data.data)
      setCurrentIndex(0)
    } catch (error) {
      console.error('Error fetching unconfirmed removed products:', error)
      toast.error(formatMessage({id: 'REMOVED_PRODUCTS.FETCH_ERROR'}))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (show) {
      fetchUnconfirmedProducts()
    }
  }, [show])

  const handleConfirm = async () => {
    if (!auth?.token || currentIndex >= products.length) return

    const currentProduct = products[currentIndex]
    setConfirming(true)

    try {
      await confirmRemovedProduct(currentProduct.removed_po_item_id, auth.token, language)
      toast.success(formatMessage({id: 'REMOVED_PRODUCTS.CONFIRMATION_SUCCESS'}))

      // Remove confirmed product from list
      const updatedProducts = products.filter((p) => p.removed_po_item_id !== currentProduct.removed_po_item_id)

      if (updatedProducts.length === 0) {
        // All products confirmed
        onAllConfirmed()
        setProducts([])
        setCurrentIndex(0)
      } else {
        // Move to next product or stay at current index if we removed current
        setProducts(updatedProducts)
        if (currentIndex >= updatedProducts.length) {
          setCurrentIndex(updatedProducts.length - 1)
        }
      }
    } catch (error: any) {
      console.error('Error confirming removed product:', error)
      toast.error(
        error.response?.data?.message ||
          formatMessage({id: 'REMOVED_PRODUCTS.CONFIRMATION_ERROR'})
      )
    } finally {
      setConfirming(false)
    }
  }

  const currentProduct = products[currentIndex]

  if (!show || loading) {
    return null
  }

  if (products.length === 0) {
    return null
  }

  return (
    <Modal
      show={show}
      backdrop='static'
      keyboard={false}
      centered
      size='lg'
      className='removed-products-blocking-modal'
    >
      <Modal.Header className='bg-warning text-dark'>
        <Modal.Title>
          {formatMessage({id: 'REMOVED_PRODUCTS.BLOCKING_MODAL_TITLE'})}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {currentProduct && (
          <div className='removed-product-details'>
            <div className='row mb-3'>
              <div className='col-md-4 text-center'>
                <img
                  src={currentProduct.image}
                  alt={currentProduct.product_name}
                  className='img-fluid rounded'
                  style={{maxHeight: '200px', objectFit: 'contain'}}
                />
              </div>
              <div className='col-md-8'>
                <h5 className='mb-2'>{currentProduct.product_name}</h5>
                <div className='mb-2'>
                  <strong>{formatMessage({id: 'REMOVED_PRODUCTS.BARCODE'})}:</strong>{' '}
                  {currentProduct.barcode}
                </div>
                <div className='mb-2'>
                  <strong>{formatMessage({id: 'REMOVED_PRODUCTS.MODEL'})}:</strong>{' '}
                  {currentProduct.model}
                </div>
                <div className='mb-2'>
                  <strong>{formatMessage({id: 'REMOVED_PRODUCTS.QUANTITY'})}:</strong>{' '}
                  {currentProduct.quantity}
                </div>
                <div className='mb-2'>
                  <strong>{formatMessage({id: 'REMOVED_PRODUCTS.PO_ID'})}:</strong> #
                  {currentProduct.po_id}
                </div>
                <div className='mb-2'>
                  <strong>{formatMessage({id: 'REMOVED_PRODUCTS.REMOVAL_REASON'})}:</strong>{' '}
                  {currentProduct.removal_reason}
                </div>
                <div className='mb-2'>
                  <strong>{formatMessage({id: 'REMOVED_PRODUCTS.REMOVED_DATE'})}:</strong>{' '}
                  {new Date(currentProduct.removed_date).toLocaleDateString()}
                </div>
              </div>
            </div>

            <div className='alert alert-info'>
              {formatMessage({id: 'REMOVED_PRODUCTS.CONFIRMATION_MESSAGE'})}
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant='success'
          onClick={handleConfirm}
          disabled={confirming || !currentProduct}
          size='lg'
          className='w-100'
        >
          {confirming ? (
            <>
              <Spinner animation='border' size='sm' className='me-2' />
              {formatMessage({id: 'REMOVED_PRODUCTS.CONFIRMING'})}
            </>
          ) : (
            formatMessage({id: 'REMOVED_PRODUCTS.CONFIRM_BUTTON'})
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default RemovedProductsBlockingModal



